import {
  Dispatch,
  FC,
  SetStateAction,
  useState,
  useEffect,
  useRef,
} from "react";
import { convertDateToUTC } from "../../utils/utils";
import moment from "moment";
import EDD from "../../types/EDD";
import EDDSelector from "../edd-selector";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { Box } from "@mui/material";
import SuggestionConfirmation from "../suggestion-confirmation";
import IntelligenceText from "../../types/IntelligenceText";
import IntelligenceTextDisplay from "../intelligence-text";
import { ActionTrack } from "../../types/Analytics";
import { useMutation, useQueryClient } from "react-query";
import { patchEDD } from "../../api/endpoints";
import { useUpdatePatientCache } from "../../hooks/use-update-patient-cache";

interface EDDSelectorInlineProps {
  currentUserEDD?: EDD;
  suggestedEDD?: EDD;
  intelText?: IntelligenceText;
  patientID: number;
  enableDischargeTimeOfDay: boolean;
  analytics: AnalyticsBrowser;
  setShowErrorToast: Dispatch<SetStateAction<boolean>>;
  disabled?: boolean;
}

const EDDSelectorInline: FC<EDDSelectorInlineProps> = ({
  currentUserEDD,
  suggestedEDD,
  intelText,
  patientID,
  enableDischargeTimeOfDay,
  analytics,
  setShowErrorToast,
  disabled,
}) => {
  const [currentEDD, setCurrentEDD] = useState(
    currentUserEDD ? currentUserEDD.edd_value : null
  );
  const [isDischargeAM, setIsDischargeAM] = useState(false);
  const [suggestionActionTaken, setSuggestionActionTaken] = useState(false);

  // Sync state when edd prop changes
  useEffect(() => {
    if (currentUserEDD) {
      setCurrentEDD(currentUserEDD.edd_value);
    }
  }, [currentUserEDD]);

  const queryClient = useQueryClient();
  const { upsertEDD } = useUpdatePatientCache();

  const updatePatientEDDMutation = useMutation(patchEDD, {
    onSuccess: (data) => {
      // Update the list cache directly with the returned data
      upsertEDD(patientID, data);

      // Still invalidate single-patient queries for QCard
      queryClient.invalidateQueries({ queryKey: ["edd"] });
    },
    onError: () => {
      if (currentUserEDD) {
        const currentUserEDDFormatted = moment(currentUserEDD.edd_value).format(
          "MM/DD/YYYY"
        );
        setCurrentEDD(currentUserEDDFormatted);
        setSuggestionActionTaken(false);
      }
      setShowErrorToast(true);
    },
  });

  const handleSetCurrentEDD = (edd_value) => {
    const edd = moment(edd_value).format("YYYY-MM-DD");
    const e = convertDateToUTC(new Date(edd));
    setCurrentEDD(moment(e).format("MM/DD/YYYY"));
    setSuggestionActionTaken(true);
  };

  const handleSuggestionAction = (status: string, suggestion: EDD) => {
    const edd = moment(suggestion.edd_value).format("YYYY-MM-DD");

    if (status === "ACCEPTED") {
      const e = convertDateToUTC(new Date(edd));
      setCurrentEDD(moment(e).format("MM/DD/YYYY"));
      setIsDischargeAM(suggestion.is_am_discharge);
    }

    setSuggestionActionTaken(true);

    updatePatientEDDMutation.mutate({
      id: suggestion.id,
      edd_value: edd,
      status: status,
    });

    analytics?.track(ActionTrack.SUGGESTION_ACTION, {
      type: "Edd",
      suggestion_id: suggestion.id,
      suggestion_value: edd,
      status,
    });
  };

  let shouldDisplaySuggestion = suggestedEDD !== null;

  if (suggestedEDD && intelText) {
    shouldDisplaySuggestion =
      new Date(suggestedEDD.qv_created_ts) > new Date(intelText.qv_created_ts);
  }

  const updatedEDD = null;

  /** EDD field + suggestion/intel row — popovers anchor below the full stack. */
  const inlineRowAnchorRef = useRef<HTMLDivElement>(null);

  const finalCurrentEDD = updatedEDD ? updatedEDD : currentEDD;

  // only show the suggestion if we haven't taken an action (including optimistic updates)
  const hasSuggestion =
    suggestedEDD &&
    !suggestionActionTaken &&
    !updatedEDD &&
    shouldDisplaySuggestion;

  const hasIntelText =
    intelText &&
    !shouldDisplaySuggestion &&
    !updatedEDD &&
    !suggestionActionTaken;

  return (
    <Box
      ref={inlineRowAnchorRef}
      sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}
    >
      <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
        <EDDSelector
          currentEDD={finalCurrentEDD}
          setCurrentEDD={handleSetCurrentEDD}
          currentUserEDD={currentUserEDD}
          patientID={patientID}
          isDischargeAM={isDischargeAM}
          setIsDischargeAM={setIsDischargeAM}
          enableDischargeTimeOfDay={enableDischargeTimeOfDay}
          isInline
          analytics={analytics}
          setShowErrorToast={setShowErrorToast}
          hasSuggestion={hasSuggestion}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {hasSuggestion ? (
          <SuggestionConfirmation
            suggestion={suggestedEDD}
            suggestionType={"edd"}
            updateFn={handleSuggestionAction}
            disabled={disabled}
            explainabilityAnchorRef={inlineRowAnchorRef}
          />
        ) : hasIntelText ? (
          <IntelligenceTextDisplay
            intelligenceText={intelText}
            popoverAnchorRef={inlineRowAnchorRef}
          />
        ) : (
          <></>
        )}
      </Box>
    </Box>
  );
};

export default EDDSelectorInline;
